import { type CSSProperties, useEffect, useMemo } from "react";
import {
  createMediator,
  type EventNode,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import {
  AD_DIMENSION_MAX_PX,
  type Ad,
  type BmgBannerProps,
  fetchAd,
} from "@/shared/ui/ad/bmgAdModel";
import { safeAdImageUrl, safeAdUrl } from "@/shared/ui/ad/safeAdUrl";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

type AdEvent = { type: "ad.load" } | { type: "ad.loaded"; ad: Ad | null };
type AdEffect = { type: "load" };
export interface BmgBannerPresentation {
  node: EventNode<TypedEvent>;
  displayedAd: Ad | null | undefined;
  imageUrl: string | null;
  clickUrl: string | null;
  frameClassName: string;
  frameStyle: CSSProperties;
  presentationKey: string;
  shouldRender: boolean;
  priority: boolean;
  concealR18: boolean;
}

export function useBmgBannerPresenter({
  slot = "default",
  category = "safe",
  reserveEmptySpace = false,
  slotDimensions,
  priority = false,
  concealR18 = false,
}: BmgBannerProps): BmgBannerPresentation {
  const mediator = useMemo(() => {
    const instance = createMediator<Ad | null | undefined, AdEvent, AdEffect>(
      undefined,
      (_state, event) =>
        event.type === "ad.load"
          ? { status: "handled", state: undefined, effects: [{ type: "load" }] }
          : { status: "handled", state: event.ad },
      {
        runEffect(): void {
          void fetchAd(slot, category).then((ad) => {
            instance.dispatch({ type: "ad.loaded", ad });
          });
        },
      },
    );
    return instance;
  }, [slot, category]);
  useHierarchyLifetime(mediator);
  const displayedAd = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (event.type !== "ad.load" && event.type !== "ad.loaded")
      return "unhandled";
    return mediator.dispatch(event as AdEvent).status === "handled"
      ? "handled"
      : "unhandled";
  });
  useEffect(() => {
    if (!mediator.isDisposed) node.dispatch({ type: "ad.load" });
  }, [mediator, node]);
  // 取得済みなら URL を検証する。相対パスは配信ベースに解決し、許可ドメイン以外や
  // 危険なスキームは弾く。取得済みで画像URLが不正なら空の予約枠として扱う。
  const imageUrl = displayedAd ? safeAdImageUrl(displayedAd.image_url) : null;
  const shouldRender = !(
    displayedAd !== undefined &&
    !imageUrl &&
    !reserveEmptySpace
  );

  // click_url が null（リンク無し広告）/ 不正URLのときはリンクを作らず、
  // <img> 単体で描画する（アンカー無し・onClick 無しでクリックしても何も起きない）。
  const clickUrl =
    imageUrl && displayedAd ? safeAdUrl(displayedAd.click_url) : null;

  const validSlotDimensions =
    slotDimensions &&
    [slotDimensions.width, slotDimensions.height].every(
      (dimension) =>
        Number.isInteger(dimension) &&
        dimension >= 1 &&
        dimension <= AD_DIMENSION_MAX_PX,
    );
  const reservedWidth = validSlotDimensions ? slotDimensions.width : 468;
  const reservedHeight = validSlotDimensions ? slotDimensions.height : 60;
  const frameWidth = reserveEmptySpace
    ? reservedWidth
    : (displayedAd?.image_width ?? reservedWidth);
  const frameHeight = reserveEmptySpace
    ? reservedHeight
    : (displayedAd?.image_height ?? reservedHeight);
  const frameClassName = "block w-full";
  const frameStyle: CSSProperties = {
    aspectRatio: `${frameWidth} / ${frameHeight}`,
    maxWidth: `min(${frameWidth}px, ${(50 * frameWidth) / frameHeight}svh)`,
    maxHeight: "50svh",
  };

  const presentationKey = [
    category,
    slot,
    displayedAd?.id ?? "",
    imageUrl ?? "",
    concealR18 ? "concealed" : "visible",
  ].join("\u0000");

  return {
    node,
    displayedAd,
    imageUrl,
    clickUrl,
    frameClassName,
    frameStyle,
    presentationKey,
    shouldRender,
    priority,
    concealR18,
  };
}
